import PanelBoxLayout from 'components/layout/panelBox';

import * as S from './styles';
import DangerButton from 'components/form/buttons/danger';
import { useActions } from 'common/hooks/useActions';
import QuestionModal from 'components/modal/question';
import { useState } from 'react';
import SecundButton from 'components/form/buttons/secundButton';
import StatusChip from 'components/statusChip';
import Thumb from 'components/thumb';
import Value from 'components/value';
import DetailGrid, { DetailItem } from 'components/detailGrid';
import { formatDateTime, MISSING } from 'common/helpers/format';

import TicketDetailAttachments from './lib/attachments';

interface IProps {
  data: any;
}

export default function TicketDetailInfo({ data }: IProps) {
  const { ticketChangeStatusRequestAction, ticketDetailRequestActionNew } = useActions();
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);

  const handleShowCloseTicket = () => {
    setShowCloseModal(true);
  };

  const handleHideCloseTicket = () => {
    setShowCloseModal(false);
  };

  const handleCloseTicket = (note?: string) => {
    ticketChangeStatusRequestAction(data.id, 'CLOSED_BY_ADMIN', note, () => {
      ticketDetailRequestActionNew(data.id);
      handleHideCloseTicket();
    });
  };

  const handleChangeStatusTicket = (status: string) => {
    ticketChangeStatusRequestAction(data.id, status, undefined, () => {
      ticketDetailRequestActionNew(data.id);
    });
  };

  /*
   * Close is a danger button and now looks like one. Both call sites used
   * to override its colour through className — "secondary" in one branch,
   * "primary" in the other — which under the old buttons meant something
   * about emphasis and under the redesigned ones means a colour. The result
   * was that closing a ticket rendered as a neutral outline in one state and
   * a teal call-to-action in the other. It is the same act either way.
   *
   * A closed ticket has no actions, and gets none.
   *
   * It used to render a disabled button reading "Closed by user" pinned to the
   * bottom-right — a control that could not be pressed, saying exactly what
   * the status already said. With the status chip in the section head the
   * button was the same sentence twice, once in a shape that promises it can
   * be clicked.
   */
  const actions =
    data?.status === 'NEW' ? (
      <>
        <DangerButton onClick={handleShowCloseTicket} className="sm">
          Close
        </DangerButton>
        <SecundButton className="sm" onClick={() => handleChangeStatusTicket('IN_PROGRESS')}>
          Progress
        </SecundButton>
      </>
    ) : data?.status === 'IN_PROGRESS' ? (
      <DangerButton onClick={handleShowCloseTicket} className="sm">
        Close
      </DangerButton>
    ) : null;

  return (
    <S.Content>
      <PanelBoxLayout
        className="flush"
        contentStyle={{ padding: 0 }}
        title={data?.id ? `Ticket #${data.id}` : 'Ticket'}
        afterTitle={<StatusChip domain="ticketStatus" value={data?.status} />}
        info={actions}
      >
        <DetailGrid>
          <DetailItem label="Requester">
            <S.Grid>
              {/*
               * Thumb, not an <img> with a default-avatar fallback: a profile
               * picture is an imgproxy URL like every other image in the
               * panel, and every one of them 404s, so the fallback never ran
               * and the cell drew the browser's broken-image glyph.
               */}
              <Thumb src={data?.user?.profilePicture} name={data?.user?.displayName} />
              <S.Name>
                <Value fallback={MISSING.provided}>{data?.user?.displayName}</Value>
              </S.Name>
            </S.Grid>
          </DetailItem>

          <DetailItem label="Employee ID">
            <Value mono>{data?.user?.id}</Value>
          </DetailItem>

          <DetailItem label="Email">
            <Value fallback={MISSING.provided}>{data?.user?.mail}</Value>
          </DetailItem>

          <DetailItem label="Ticket number">
            <Value mono fallback={MISSING.assigned}>
              {data?.number}
            </Value>
          </DetailItem>

          <DetailItem label="Priority">
            <StatusChip domain="ticketPriority" value={data?.priority} fallback={<Value />} />
          </DetailItem>

          {/*
           * The category the requester filed under — joined by the detail
           * endpoint, translated to the default language by the backend, and
           * until now dropped on the floor by this screen. It is the first
           * thing a supporter needs to know about a ticket and the list has
           * always shown it, so the detail disagreeing with the list was the
           * confusing part.
           */}
          <DetailItem label="Category">
            <Value fallback={MISSING.none}>{data?.category?.title}</Value>
          </DetailItem>

          <DetailItem label="Opened">
            <Value mono>{formatDateTime(data?.createdAt)}</Value>
          </DetailItem>

          <DetailItem label="Subject" wide>
            <Value fallback={MISSING.provided}>{data?.subject?.replace(/\r\n/g, '\n')}</Value>
          </DetailItem>

          <DetailItem label="Description" wide prose>
            <Value fallback={MISSING.provided}>{data?.description?.replace(/\r\n/g, '\n')}</Value>
          </DetailItem>

          <DetailItem label="Attachments" wide>
            <TicketDetailAttachments data={data?.attachments} />
          </DetailItem>

          {data?.closeNote ? (
            <DetailItem label="Close note" wide prose>
              {data.closeNote}
            </DetailItem>
          ) : (
            <></>
          )}
        </DetailGrid>
      </PanelBoxLayout>

      <QuestionModal
        show={showCloseModal}
        handleClose={handleHideCloseTicket}
        model="delete"
        title="Close Ticket"
        text="Do you want to Close this Ticket?"
        handleSuccess={handleCloseTicket}
        successButtonText="Close"
        note
        notePlaceholder="Please enter close reason"
      />
    </S.Content>
  );
}
